import Config from '@/config'
import { logger } from '@/lib/logger'
import type { ApisauceInstance } from 'apisauce'
import { create } from 'apisauce'
import { authRequestInterceptor } from './interceptors/auth'
import type {
	AppointmentResultReportResponse,
	CreateAppointmentSchedulingLinkResponse,
	RescheduleAppointmentResponse,
	ScheduleActionsResponse,
	ScheduledVrdhCall,
	UpcomingPatientAppointment,
	VrdhReschedulingRequest,
	VrdhSchedulingRequest,
	VrdhSchedulingSlot,
} from './types/ProviderApi'

export class ProviderApi {
	_apiConfig: ApisauceInstance

	constructor() {
		this._apiConfig = create({
			baseURL: Config.PROVIDER_API_URL_BASE,
			timeout: 10000,
		})

		this._apiConfig.axiosInstance.interceptors.request.use(authRequestInterceptor)
	}

	fetchUpcomingAppointment() {
		return this._apiConfig
			.get('/appointments/next-appointment')
			.then((response) => response.data as UpcomingPatientAppointment)
			.catch((error) => {
				logger.error(new Error('failed_to_get_upcoming_appointment', { cause: error }))
				return {} as UpcomingPatientAppointment
			})
	}

	confirmAppointment(appointmentId: number) {
		return this._apiConfig
			.post(`/appointments/${appointmentId}/confirm`)
			.catch((error) => {
				logger.error(new Error('failed_to_confirm_appointment', { cause: error }))
			})
	}

	createAppointmentSchedulingLink() {
		return this._apiConfig
			.post('/schedule/patient-requested-appointment')
			.then((response) => response.data as CreateAppointmentSchedulingLinkResponse)
			.catch((error) => {
				logger.error(new Error('failed_to_create_scheduling_link', { cause: error }))
				return {} as CreateAppointmentSchedulingLinkResponse
			})
	}

	cancelAppointment(appointmentId: number) {
		return this._apiConfig
			.post(`/appointments/${appointmentId}/patient-cancel`)
			.catch((error) => {
				logger.error(new Error('failed_to_cancel_appointment', { cause: error }))
			})
	}

	rescheduleAppointment(appointmentId: number) {
		return this._apiConfig
			.post(`/confirmations/${appointmentId}/patient-reschedule`)
			.then((response) => response.data as RescheduleAppointmentResponse)
			.catch((error) => {
				logger.error(new Error('failed_to_reschedule_appointment', { cause: error }))
				return {} as RescheduleAppointmentResponse
			})
	}

	fetchSchedulingActions() {
		return this._apiConfig
			.get('/schedule/scheduling-actions')
			.then((response) => response.data as ScheduleActionsResponse)
			.catch((error) => {
				logger.error(new Error('failed_to_fetch_schedule_actions', { cause: error }))
				return { canScheduleAppointment: false } as ScheduleActionsResponse
			})
	}

	fetchVrdhSchedulingSlots() {
		return this._apiConfig
			.get('/vrdh/appointments/slots/open')
			.then((response) => response.data as VrdhSchedulingSlot[])
			.catch((error) => {
				logger.error(new Error('failed_to_fetch_vrdh_slots', { cause: error }))
				return [] as VrdhSchedulingSlot[]
			})
	}

	fetchUpcomingVrdhCalls() {
		return this._apiConfig
			.get('/vrdh/appointments/upcoming')
			.then((response) => response.data as ScheduledVrdhCall[])
			.catch((error) => {
				logger.error(new Error('failed_to_fetch_vrdh_calls', { cause: error }))
			})
	}

	bookVrdhSchedulingSlot(slot: VrdhSchedulingRequest) {
		return this._apiConfig.post('/vrdh/appointments/book', slot).catch((error) => {
			logger.error(new Error('failed_to_book_vrdh_slot', { cause: error }))
			throw error
		})
	}

	rescheduleVrdhSchedulingSlot(rescheduleRequest: VrdhReschedulingRequest) {
		return this._apiConfig
			.post('/vrdh/appointments/reschedule', rescheduleRequest)
			.catch((error) => {
				logger.error(new Error('failed_to_reschedule_vrdh_slot', { cause: error }))
				throw error
			})
	}

	fetchLatestAppointmentResults() {
		return this._apiConfig.get('/appointment-results/latest').then((response) => {
			if (!response.ok) {
				return null
			}

			return response.data as AppointmentResultReportResponse
		})
	}
}

export const providerApi = new ProviderApi()
